import { CSSProperties, useState, useEffect } from 'react';
import { ImagesContainer, MarkerMap } from './assetReportProfile.styles';
import NoMapPlaceholder from '../../../assets/images/img-no-map.svg';

const mapContainerStyle = {
  width: '430px',
  height: '100%',
  gridColumn: 1,
  gridRow: 1,
  borderRadius: '8px',
  display: 'flex',
  alignSelf: 'center',
  justifySelf: 'center',
  overflow: 'hidden',
};

export const AssetProfileMaps = ({
  center,
}: {
  center: { lat: number | null; lng: number | null };
}) => {
  const smallerImageSize = '250x125';
  const mainImageSize = '430x270';
  const mapScale = 2;
  const [hasValidCoordinates, setHasValidCoordinates] = useState(false);

  useEffect(() => {
    // Validate coordinates
    const isValid =
      typeof center.lat === 'number' &&
      typeof center.lng === 'number' &&
      !isNaN(center.lat) &&
      !isNaN(center.lng) &&
      center.lat >= -90 &&
      center.lat <= 90 &&
      center.lng >= -180 &&
      center.lng <= 180;

    setHasValidCoordinates(isValid);

    if (!isValid) {
      console.warn('Invalid coordinates:', center);
    }
  }, [center]);

  // If coordinates are invalid, show all placeholders
  if (!hasValidCoordinates) {
    return (
      <ImagesContainer aria-label="No map available">
        <MarkerMap>
          <img src={NoMapPlaceholder} alt="Map" style={mapContainerStyle} />
        </MarkerMap>
        <img
          src={NoMapPlaceholder}
          alt="Street View"
          style={{
            borderRadius: '13px',
            gridColumn: 2,
            gridRow: 1,
            display: 'flex',
            alignSelf: 'center',
            justifySelf: 'center',
            width: '100%',
            height: '120px',
            overflow: 'hidden',
          }}
        />
        <img
          src={NoMapPlaceholder}
          alt="Satellite View"
          style={{
            borderRadius: '13px',
            gridColumn: 2,
            gridRow: 2,
            display: 'flex',
            alignSelf: 'center',
            justifySelf: 'center',
            width: '100%',
            height: '120px',
            overflow: 'hidden',
          }}
        />
      </ImagesContainer>
    );
  }

  // Format coordinates to 6 decimal places for API requests
  const lat = center.lat!.toFixed(6);
  const lng = center.lng!.toFixed(6);

  return (
    <ImagesContainer aria-label="images-container">
      <MarkerMap>
        <ImageWithFallback
          src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=16&size=${mainImageSize}&scale=${mapScale}&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_STREET_VIEW_API_KEY}`}
          alt="Map"
          style={mapContainerStyle}
          fallbackSrc={NoMapPlaceholder}
        />
      </MarkerMap>

      <ImageWithFallback
        src={`https://maps.googleapis.com/maps/api/streetview?fov=100&size=${smallerImageSize}&scale=1&location=${lat},${lng}&key=${process.env.REACT_APP_STREET_VIEW_API_KEY}`}
        alt="Street View"
        style={{
          borderRadius: '8px',
          gridColumn: 2,
          gridRow: 1,
          display: 'flex',
          alignSelf: 'center',
          justifySelf: 'center',
          overflow: 'hidden',
        }}
        fallbackSrc={NoMapPlaceholder}
      />

      <ImageWithFallback
        src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&scale=1&zoom=15&size=${smallerImageSize}&markers=size:small%7Ccolor:red%7Clabel:S%7C${lat},${lng}&maptype=satellite&key=${process.env.REACT_APP_STREET_VIEW_API_KEY}`}
        alt="Satellite View"
        style={{
          borderRadius: '8px',
          gridColumn: 2,
          gridRow: 2,
          display: 'flex',
          alignSelf: 'center',
          justifySelf: 'center',
          overflow: 'hidden',
        }}
        fallbackSrc={NoMapPlaceholder}
      />
    </ImagesContainer>
  );
};

const ImageWithFallback = ({
  src,
  fallbackSrc,
  alt,
  style,
}: {
  src: string;
  fallbackSrc: string;
  alt: string;
  style: CSSProperties;
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Reset state when src changes
    setCurrentSrc(src);
    setHasError(false);
  }, [src]);

  const handleImageError = () => {
    if (!hasError && fallbackSrc) {
      console.warn(`Failed to load image: ${src}`);
      setCurrentSrc(fallbackSrc);
      setHasError(true);
    }
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      onError={handleImageError}
      style={style}
      crossOrigin="anonymous" // Add CORS support
    />
  );
};
